import { env } from '@/app/env'
import { telemetry } from '@/app/telemetry'
import { sessionStore, type SessionStore } from '@/auth/sessionStore'
import { ApiError, ForbiddenError } from './errors'
import type { TokenPair } from './pending'

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  /** Route template; `:name` segments are filled from `params`. */
  params?: Record<string, string | number>
  query?: Record<string, string | number | boolean | undefined>
  body?: unknown
  headers?: Record<string, string>
  /** false for public endpoints (pairing, refresh): no Bearer and no refresh-on-401. */
  auth?: boolean
  signal?: AbortSignal
}

export interface ClientDeps {
  baseUrl: string
  session: SessionStore
  fetch?: typeof fetch
  uuid?: () => string
  onApiError?: (report: {
    method: string
    pathTemplate: string
    status: number
    requestId: string
  }) => void
}

function fillPath(template: string, params: RequestOptions['params']): string {
  return template.replace(/:([A-Za-z_]+)/g, (_, name: string) =>
    encodeURIComponent(String(params?.[name] ?? '')),
  )
}

export function createClient({
  baseUrl,
  session,
  fetch: fetchImpl = (...a) => fetch(...a),
  uuid = () => crypto.randomUUID(),
  onApiError,
}: ClientDeps) {
  const forbiddenListeners = new Set<(e: ForbiddenError) => void>()
  let refreshing: Promise<boolean> | null = null

  /** Single flight: concurrent 401s share one refresh. Resolves false when the session is dead. */
  function refresh(): Promise<boolean> {
    refreshing ??= (async () => {
      const refreshToken = session.getRefreshToken()
      if (!refreshToken) return false
      try {
        const tokens = await send<TokenPair>('/users/refresh', {
          method: 'POST',
          body: { refresh_token: refreshToken },
          auth: false,
        })
        session.setTokens(tokens)
        return true
      } catch (e) {
        // Only an auth failure ends the session; a network blip must not log the user out.
        if (e instanceof ApiError && e.status >= 400 && e.status < 500) return false
        throw e
      }
    })().finally(() => {
      refreshing = null
    })
    return refreshing
  }

  async function send<T>(template: string, opts: RequestOptions): Promise<T> {
    const method = opts.method ?? 'GET'
    const requestId = uuid()
    const url = new URL(baseUrl + fillPath(template, opts.params))
    for (const [k, v] of Object.entries(opts.query ?? {})) {
      if (v !== undefined && v !== '') url.searchParams.set(k, String(v))
    }
    const headers: Record<string, string> = { 'X-Request-ID': requestId, ...opts.headers }
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json'
    const token = session.getAccessToken()
    if (opts.auth !== false && token) headers.Authorization = `Bearer ${token}`

    const res = await fetchImpl(url, {
      method,
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: opts.signal,
    })
    if (res.ok) return (res.status === 204 ? undefined : await res.json()) as T

    const body = (await res.json().catch(() => null)) as {
      error?: string
      required_capability?: string
    } | null
    const message = body?.error ?? res.statusText
    // 401 is handled (and retried) by the caller; everything else is reported here.
    if (res.status !== 401) {
      onApiError?.({ method, pathTemplate: template, status: res.status, requestId })
    }
    if (res.status === 403 && body?.required_capability) {
      throw new ForbiddenError(message, requestId, body.required_capability)
    }
    throw new ApiError(res.status, message, requestId, body?.error)
  }

  const is401 = (e: unknown): e is ApiError => e instanceof ApiError && e.status === 401

  /** The session is gone: drop the tokens (RequireAuth then sends the user to /login). */
  function endSession(template: string, opts: RequestOptions, e: ApiError): never {
    session.clear()
    onApiError?.({
      method: opts.method ?? 'GET',
      pathTemplate: template,
      status: 401,
      requestId: e.requestId,
    })
    throw e
  }

  async function requestOnce<T>(template: string, opts: RequestOptions): Promise<T> {
    if (opts.auth === false) return send<T>(template, opts)

    // Reload with only a refresh token (or another tab just logged in): get an access token first.
    if (!session.getAccessToken() && session.getRefreshToken() && !(await refresh())) {
      return endSession(template, opts, new ApiError(401, 'Sessão expirada', ''))
    }

    try {
      return await send<T>(template, opts)
    } catch (e) {
      if (!is401(e)) throw e
      // First 401: refresh once and retry. A second 401 means the session is gone.
      if (!(await refresh())) return endSession(template, opts, e)
      try {
        return await send<T>(template, opts)
      } catch (retryError) {
        if (is401(retryError)) return endSession(template, opts, retryError)
        throw retryError
      }
    }
  }

  async function request<T>(template: string, opts: RequestOptions = {}): Promise<T> {
    try {
      return await requestOnce<T>(template, opts)
    } catch (e) {
      if (e instanceof ForbiddenError) forbiddenListeners.forEach((l) => l(e))
      throw e
    }
  }

  return {
    request,
    get: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
      request<T>(path, { ...opts, method: 'GET' }),
    post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
      request<T>(path, { ...opts, method: 'POST', body }),
    put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
      request<T>(path, { ...opts, method: 'PUT', body }),
    patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
      request<T>(path, { ...opts, method: 'PATCH', body }),
    delete: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
      request<T>(path, { ...opts, method: 'DELETE' }),
    /** Called for every 403 carrying `required_capability`. Returns an unsubscribe. */
    onForbidden(listener: (e: ForbiddenError) => void) {
      forbiddenListeners.add(listener)
      return () => {
        forbiddenListeners.delete(listener)
      }
    },
  }
}

export type ApiClient = ReturnType<typeof createClient>

export const api = createClient({
  baseUrl: env.apiBaseUrl,
  session: sessionStore,
  onApiError: (r) => telemetry.captureApiError(r),
})
